import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Copy, Download, Eye, EyeOff } from "lucide-react";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { LpfrLicensesSection } from "./LpfrLicensesSection";

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString("sr-Latn-RS") : "—");

/** Podešavanja → Fiskalizacija → L-PFR: licenca (uvek vidljiva), preuzimanje i svi L-PFR nalozi/uređaji firme. */
export function LpfrTab() {
  const q = useQuery({ queryKey: ["lpfr-overview"], queryFn: () => api.getLpfrOverview(), retry: false });
  const [show, setShow] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);
  const [more, setMore] = useState(false);

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* ignore */
    }
  };

  if (q.isLoading) return <Skeleton className="h-40 w-full" />;
  if (q.isError || !q.data) {
    return <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">Učitavanje nije uspelo. Licencu vide samo vlasnik i administrator.</p>;
  }
  const d = q.data;

  if (!d.module_active) {
    return (
      <div className="rounded-xl border border-border bg-surface p-4 text-sm text-slate-600">
        Modul <b>L-PFR</b> nije aktivan za ovu firmu. Aktivirajte ga u delu „Moj paket“ da bi dobili licencu i program za preuzimanje.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <section className="space-y-2 rounded-xl border border-border bg-surface p-4">
        <h2 className="text-sm font-semibold text-slate-800">Vaša L-PFR licenca</h2>
        <p className="text-xs text-slate-500">Licenca je uvek ovde. Unosi se u L-PFR program, ili se L-PFR sam prijavi vašim HAFiskal nalogom.</p>
        <div className="flex flex-wrap items-center gap-2">
          <code className="rounded-lg bg-slate-100 px-3 py-2 text-sm">{show ? d.license?.key : `${d.license?.key_prefix}••••••••••••`}</code>
          <Button size="sm" variant="secondary" onClick={() => setShow(!show)}>
            {show ? <EyeOff className="mr-1.5 h-4 w-4" aria-hidden /> : <Eye className="mr-1.5 h-4 w-4" aria-hidden />}
            {show ? "Sakrij" : "Prikaži"}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => d.license && copy(d.license.key, "key")}>
            <Copy className="mr-1.5 h-4 w-4" aria-hidden />
            {copied === "key" ? "Kopirano" : "Kopiraj"}
          </Button>
        </div>
      </section>

      <section className="space-y-2 rounded-xl border border-border bg-surface p-4">
        <h2 className="text-sm font-semibold text-slate-800">Preuzimanje programa</h2>
        <div className="flex flex-wrap gap-2">
          <a href={d.download?.windows_url} className="inline-flex h-9 items-center rounded-lg bg-brand px-3 text-sm font-medium text-white">
            <Download className="mr-1.5 h-4 w-4" aria-hidden /> Windows
          </a>
          <a href={d.download?.linux_url} className="inline-flex h-9 items-center rounded-lg border border-border px-3 text-sm font-medium text-slate-700">
            <Download className="mr-1.5 h-4 w-4" aria-hidden /> Linux
          </a>
        </div>
        <p className="text-xs text-slate-400">
          Windows: preuzeto {d.downloads.windows.count}×{d.downloads.windows.version ? ` (verzija ${d.downloads.windows.version})` : ""}, poslednji put {fmt(d.downloads.windows.last)} ·
          Linux: preuzeto {d.downloads.linux.count}×, poslednji put {fmt(d.downloads.linux.last)}
        </p>
      </section>

      <section className="space-y-2 rounded-xl border border-border bg-surface p-4">
        <h2 className="text-sm font-semibold text-slate-800">Moji L-PFR nalozi i uređaji</h2>
        {d.installations.length === 0 ? (
          <p className="text-sm text-slate-500">Još nijedan L-PFR nije potpisao račun. Kad počnete da fiskalizujete, ovde se pojavljuje svaki uređaj.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500">
                <tr><th className="py-1 pr-3">Kartica (JID)</th><th className="pr-3">Gde radi</th><th className="pr-3">Računa</th><th>Poslednji račun</th></tr>
              </thead>
              <tbody>
                {d.installations.map((i, n) => (
                  <tr key={n} className="border-t border-border">
                    <td className="py-1.5 pr-3 font-mono">{i.jid ?? "—"}</td>
                    <td className="pr-3">{i.platform}{i.device ? ` · ${i.device}` : ""}</td>
                    <td className="pr-3">{i.invoices}</td>
                    <td>{fmt(i.last_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div>
        <button type="button" className="text-xs text-slate-400 underline" onClick={() => setMore(!more)}>
          {more ? "Sakrij dodatne licence" : "Dodatne licence (za napredne)"}
        </button>
        {more && <LpfrLicensesSection />}
      </div>
    </div>
  );
}
